import React, { useCallback, useEffect, useState, useRef, Fragment } from 'react';
import { Search, Send, MoreVertical, Phone, Video, CheckCheck, AlertTriangle, RefreshCw, MessageSquare, ArrowLeft, Loader2, Bot, Zap, CreditCard, Settings, Lock, Plus } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../ui/Button';
import { chatApi, ChatUser, ChatMessage } from '../../utils/chatApi';
interface ChatPageProps {
  instanceName: string;
  isRTL: boolean;
  language: 'ar' | 'en';
  hasConnectedWhatsApp: boolean;
  isBotEnabled: boolean;
  subscriptionActive: boolean;
  onNavigateToIntegration: () => void;
  onToggleBot: () => void;
  onNavigateToSubscription: () => void;
}
// Simple time formatter (no date-fns needed)
function formatTime(dateString: string): string {
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return '';
  }
}
function formatDate(dateString: string): string {
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return '';
  }
}
function timeAgo(dateString: string, isRTL: boolean): string {
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return '';
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return isRTL ? 'الآن' : 'now';
    if (diffMins < 60) return isRTL ? `${diffMins} د` : `${diffMins}m`;
    if (diffHours < 24) return isRTL ? `${diffHours} س` : `${diffHours}h`;
    if (diffDays < 7) return isRTL ? `${diffDays} ي` : `${diffDays}d`;
    return formatTime(dateString);
  } catch {
    return '';
  }
}
// Helper to get proper chat display name
function getChatTitle(chat: ChatUser): string {
  const {
    name,
    chatId
  } = chat;
  // If name exists, is not "Unknown", and is NOT the same as chatId → use it
  if (name && name.trim() !== '' && name !== 'Unknown' && name !== chatId) {
    return name;
  }
  // Fallback: extract readable part from chatId
  if (!chatId) return 'Unknown';
  const idPart = chatId.split('@')[0];
  const suffix = chatId.split('@')[1] || '';
  // Group chat
  if (suffix === 'g.us') {
    return name && name !== chatId ? name : `Group ${idPart.slice(-6)}`;
  }
  // LID chat
  if (suffix === 'lid') {
    return idPart || 'Contact';
  }
  // Regular WhatsApp contact — show phone number
  if (idPart && idPart !== '0') {
    return idPart;
  }
  return 'Unknown';
}
export function ChatPage({
  instanceName,
  isRTL,
  language,
  hasConnectedWhatsApp,
  isBotEnabled,
  subscriptionActive,
  onNavigateToIntegration,
  onToggleBot,
  onNavigateToSubscription
}: ChatPageProps) {
  const [chats, setChats] = useState<ChatUser[]>([]);
  const [activeChat, setActiveChat] = useState<ChatUser | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoadingChats, setIsLoadingChats] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [messageInput, setMessageInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showMobileChat, setShowMobileChat] = useState(false);
  const [apiDebug, setApiDebug] = useState<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const msgPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isEnabled = hasConnectedWhatsApp && isBotEnabled && subscriptionActive;
  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({
        behavior: 'smooth'
      });
    }, 100);
  };
  // --- FETCH CHATS ---
  const fetchChats = useCallback(async (showLoading = false) => {
    if (!isEnabled || !instanceName) return;
    if (showLoading) setIsLoadingChats(true);
    try {
      const response = await chatApi.listChats(instanceName);
      setApiDebug(JSON.stringify(response, null, 2));
      if (response.ok && Array.isArray(response.data)) {
        setChats(response.data);
      } else if (response.ok && response.data) {
        // Handle unexpected data shape
        setChats(Array.isArray(response.data) ? response.data : []);
      }
    } catch (err: any) {
      console.error('fetchChats error:', err);
      setApiDebug(`Error: ${err.message}`);
    }
    if (showLoading) setIsLoadingChats(false);
  }, [instanceName, isEnabled]);
  // --- FETCH MESSAGES ---
  const fetchMessages = useCallback(async (chatId: string, showLoading = false) => {
    if (!isEnabled || !instanceName) return;
    if (showLoading) setIsLoadingMessages(true);
    try {
      const response = await chatApi.listMessages(instanceName, chatId);
      if (response.ok && Array.isArray(response.data)) {
        const sorted = [...response.data].sort((a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime());
        setMessages(sorted);
        if (showLoading) scrollToBottom();
      }
    } catch (err) {
      console.error('fetchMessages error:', err);
    }
    if (showLoading) setIsLoadingMessages(false);
  }, [instanceName, isEnabled]);
  // --- SEND MESSAGE ---
  const handleSendMessage = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!messageInput.trim() || !activeChat || isSending) return;
    const text = messageInput.trim();
    setMessageInput('');
    setIsSending(true);
    // Optimistic update
    const tempMsg: ChatMessage = {
      id: `temp_${Date.now()}`,
      fromMe: true,
      text,
      type: 'text',
      sentAt: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempMsg]);
    scrollToBottom();
    try {
      await chatApi.sendText(instanceName, activeChat.chatId, text);
      // Refresh messages to get server state
      fetchMessages(activeChat.chatId);
    } catch (err) {
      console.error('Send error:', err);
    }
    setIsSending(false);
  };
  // --- CHAT SELECTION ---
  const handleChatSelect = (chat: ChatUser) => {
    setActiveChat(chat);
    setShowMobileChat(true);
    setMessages([]);
    fetchMessages(chat.chatId, true);
  };
  // --- POLLING: CHATS (30s) ---
  useEffect(() => {
    if (!isEnabled) return;
    fetchChats(true);
    chatPollingRef.current = setInterval(() => {
      fetchChats(false);
    }, 30000);
    return () => {
      if (chatPollingRef.current) clearInterval(chatPollingRef.current);
    };
  }, [isEnabled, fetchChats]);
  // --- POLLING: MESSAGES (10s when chat open) ---
  useEffect(() => {
    if (!isEnabled || !activeChat) {
      if (msgPollingRef.current) clearInterval(msgPollingRef.current);
      return;
    }
    msgPollingRef.current = setInterval(() => {
      fetchMessages(activeChat.chatId, false);
    }, 10000);
    return () => {
      if (msgPollingRef.current) clearInterval(msgPollingRef.current);
    };
  }, [isEnabled, activeChat, fetchMessages]);
  // --- FILTER CHATS ---
  const filteredChats = chats.filter(chat => getChatTitle(chat).toLowerCase().includes(searchQuery.toLowerCase()) || (chat.chatId || '').includes(searchQuery));
  // =============================================
  // BLOCKED STATES
  // =============================================
  if (!hasConnectedWhatsApp) {
    return <div className="flex flex-col items-center justify-center min-h-[60vh] bg-white rounded-xl border border-gray-200 p-8 text-center" data-id="element-91">
        <div className="w-20 h-20 bg-yellow-50 rounded-full flex items-center justify-center mb-6" data-id="element-92">
          <Phone className="w-10 h-10 text-yellow-500" data-id="element-93" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-94">
          {isRTL ? 'اربط رقمك أولاً' : 'Connect WhatsApp First'}
        </h2>
        <p className="text-gray-500 max-w-md mb-8" data-id="element-95">
          {isRTL ? 'يجب ربط رقم واتساب الخاص بك للبدء في استخدام المحادثات.' : 'You need to connect your WhatsApp number to start using conversations.'}
        </p>
        <Button onClick={onNavigateToIntegration} className="bg-[#4f1964] text-white" data-id="element-96">
          <Settings className="w-4 h-4 mr-2" data-id="element-97" />
          {isRTL ? 'الذهاب للإعدادات' : 'Go to Settings'}
        </Button>
      </div>;
  }
  if (!isBotEnabled) {
    return <div className="flex flex-col items-center justify-center min-h-[60vh] bg-white rounded-xl border border-gray-200 p-8 text-center" data-id="element-98">
        <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mb-6" data-id="element-99">
          <Bot className="w-10 h-10 text-blue-500" data-id="element-100" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-101">
          {isRTL ? 'فعّل البوت' : 'Enable Bot'}
        </h2>
        <p className="text-gray-500 max-w-md mb-8" data-id="element-102">
          {isRTL ? 'المساعد الذكي معطل حالياً. قم بتفعيله للوصول للمحادثات.' : 'The AI assistant is currently disabled. Enable it to access conversations.'}
        </p>
        <Button onClick={onToggleBot} className="bg-blue-600 text-white hover:bg-blue-700" data-id="element-103">
          <Zap className="w-4 h-4 mr-2" data-id="element-104" />
          {isRTL ? 'تفعيل البوت' : 'Enable Bot'}
        </Button>
      </div>;
  }
  if (!subscriptionActive) {
    return <div className="flex flex-col items-center justify-center min-h-[60vh] bg-white rounded-xl border border-gray-200 p-8 text-center" data-id="element-105">
        <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6" data-id="element-106">
          <AlertTriangle className="w-10 h-10 text-red-500" data-id="element-107" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-108">
          {isRTL ? 'الاشتراك انتهى' : 'Subscription Expired'}
        </h2>
        <p className="text-gray-500 max-w-md mb-8" data-id="element-109">
          {isRTL ? 'انتهت فترة اشتراكك. يرجى التجديد للمتابعة.' : 'Your subscription has expired. Please renew to continue.'}
        </p>
        <Button onClick={onNavigateToSubscription} className="bg-[#4f1964] text-white" data-id="element-110">
          <CreditCard className="w-4 h-4 mr-2" data-id="element-111" />
          {isRTL ? 'تجديد الاشتراك' : 'Renew Subscription'}
        </Button>
      </div>;
  }
  // =============================================
  // MAIN CHAT UI
  // =============================================
  return <div className="flex bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" style={{
    height: 'calc(100vh - 160px)',
    minHeight: '500px'
  }} dir={isRTL ? 'rtl' : 'ltr'} data-id="element-112">
      {/* LEFT: CHAT LIST */}
      <div className={`w-full md:w-80 lg:w-96 flex flex-col border-gray-200 bg-gray-50/50 ${isRTL ? 'border-l' : 'border-r'} ${showMobileChat ? 'hidden md:flex' : 'flex'}`} data-id="element-113">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 bg-white flex justify-between items-center h-14 flex-shrink-0" data-id="element-114">
          <h2 className="font-bold text-lg text-gray-800" data-id="element-115">
            {isRTL ? 'المحادثات' : 'Chats'}
          </h2>
          <button onClick={() => fetchChats(true)} className="p-2 text-gray-500 hover:text-[#4f1964] hover:bg-purple-50 rounded-full transition-colors" title={isRTL ? 'تحديث' : 'Refresh'} data-id="element-116">
            <RefreshCw className={`w-4 h-4 ${isLoadingChats ? 'animate-spin' : ''}`} data-id="element-117" />
          </button>
        </div>

        {/* Search */}
        <div className="p-3 bg-white border-b border-gray-100 flex-shrink-0" data-id="element-118">
          <div className="relative" data-id="element-119">
            <Search className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 ${isRTL ? 'right-3' : 'left-3'}`} data-id="element-120" />
            <input type="text" placeholder={isRTL ? 'بحث في المحادثات...' : 'Search chats...'} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`w-full py-2 bg-gray-100 border-none rounded-lg focus:ring-2 focus:ring-[#4f1964] text-sm ${isRTL ? 'pr-9 pl-4' : 'pl-9 pr-4'}`} data-id="element-121" />
          </div>
        </div>

        {/* Chat List */}
        <div className="flex-1 overflow-y-auto" data-id="element-122">
          {isLoadingChats && chats.length === 0 ? <div className="space-y-1 p-3" data-id="element-123">
              {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="flex items-center gap-3 p-3 rounded-lg animate-pulse" data-id="element-124">
                  <div className="w-12 h-12 bg-gray-200 rounded-full flex-shrink-0" data-id="element-125" />
                  <div className="flex-1 space-y-2" data-id="element-126">
                    <div className="h-4 bg-gray-200 rounded w-3/4" data-id="element-127" />
                    <div className="h-3 bg-gray-100 rounded w-1/2" data-id="element-128" />
                  </div>
                </div>)}
            </div> : filteredChats.length === 0 ? <div className="flex flex-col items-center justify-center h-64 text-gray-400 p-4 text-center" data-id="element-129">
              <MessageSquare className="w-12 h-12 mb-3 opacity-20" data-id="element-130" />
              <p className="text-sm" data-id="element-131">
                {isRTL ? 'لا توجد محادثات' : 'No chats found'}
              </p>
              {chats.length === 0 && !isLoadingChats && apiDebug && <details className="mt-4 w-full text-left" data-id="element-132">
                  <summary className="text-xs text-gray-400 cursor-pointer" data-id="element-133">
                    {isRTL ? 'تفاصيل الاستجابة' : 'API Response'}
                  </summary>
                  <pre className="mt-2 text-[10px] bg-gray-100 p-2 rounded overflow-auto max-h-40 text-gray-600 whitespace-pre-wrap break-all" data-id="element-134">
                    {apiDebug}
                  </pre>
                </details>}
            </div> : <div className="divide-y divide-gray-100" data-id="element-135">
              {filteredChats.map(chat => <div key={chat.chatId} onClick={() => handleChatSelect(chat)} className={`flex items-center gap-3 p-4 cursor-pointer transition-colors hover:bg-gray-50 ${activeChat?.chatId === chat.chatId ? `bg-purple-50 ${isRTL ? 'border-r-4' : 'border-l-4'} border-[#4f1964]` : ''}`} data-id="element-136">
                  {/* Avatar */}
                  <div className="flex-shrink-0" data-id="element-137">
                    {chat.avatar ? <img src={chat.avatar} alt={getChatTitle(chat)} className="w-12 h-12 rounded-full object-cover border border-gray-200" data-id="element-138" /> : <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center text-[#4f1964] font-bold text-lg" data-id="element-139">
                        {getChatTitle(chat).charAt(0).toUpperCase()}
                      </div>}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0" data-id="element-140">
                    <div className="flex justify-between items-baseline mb-1" data-id="element-141">
                      <h3 className="font-semibold text-gray-900 truncate text-sm" data-id="element-142">
                        {getChatTitle(chat)}
                      </h3>
                      {chat.lastMessageAt && <span className="text-[11px] text-gray-400 flex-shrink-0 ml-2" data-id="element-143">
                          {timeAgo(chat.lastMessageAt, isRTL)}
                        </span>}
                    </div>
                    <div className="flex justify-between items-center" data-id="element-144">
                      <p className="text-xs text-gray-500 truncate" data-id="element-145">
                        {chat.lastMessage && chat.lastMessage.trim() ? chat.lastMessage : isRTL ? '(لا توجد رسالة)' : '(no message)'}
                      </p>
                      {(chat.unreadCount ?? 0) > 0 ? <span className="bg-[#4f1964] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center flex-shrink-0 ml-2" data-id="element-146">
                          {chat.unreadCount}
                        </span> : null}
                    </div>
                  </div>
                </div>)}
            </div>}
        </div>
      </div>

      {/* RIGHT: MESSAGES */}
      <div className={`flex-1 flex flex-col ${!showMobileChat ? 'hidden md:flex' : 'flex'}`} data-id="element-147">
        {activeChat ? <>
            {/* Chat Header */}
            <div className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-4 flex-shrink-0" data-id="element-148">
              <div className="flex items-center gap-3" data-id="element-149">
                <button onClick={() => setShowMobileChat(false)} className="md:hidden text-gray-600 mr-1" data-id="element-150">
                  <ArrowLeft className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} data-id="element-151" />
                </button>
                <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center text-[#4f1964] font-bold text-sm" data-id="element-152">
                  {getChatTitle(activeChat).charAt(0).toUpperCase()}
                </div>
                <div data-id="element-153">
                  <h3 className="font-bold text-gray-900 text-sm" data-id="element-154">
                    {getChatTitle(activeChat)}
                  </h3>
                  <p className="text-[11px] text-gray-400" data-id="element-155">
                    {activeChat.chatId}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-gray-400" data-id="element-156">
                <button className="hover:text-[#4f1964] transition-colors" data-id="element-157">
                  <Phone className="w-4 h-4" data-id="element-158" />
                </button>
                <button className="hover:text-[#4f1964] transition-colors" data-id="element-159">
                  <Video className="w-4 h-4" data-id="element-160" />
                </button>
                <button className="hover:text-[#4f1964] transition-colors" data-id="element-161">
                  <MoreVertical className="w-4 h-4" data-id="element-162" />
                </button>
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#f0f2f5]" data-id="element-163">
              {isLoadingMessages && messages.length === 0 ? <div className="flex justify-center py-12" data-id="element-164">
                  <div className="flex flex-col items-center gap-3" data-id="element-165">
                    <Loader2 className="w-8 h-8 text-[#4f1964] animate-spin" data-id="element-166" />
                    <p className="text-sm text-gray-400" data-id="element-167">
                      {isRTL ? 'جاري تحميل الرسائل...' : 'Loading messages...'}
                    </p>
                  </div>
                </div> : messages.length === 0 ? <div className="flex justify-center py-12" data-id="element-168">
                  <p className="text-sm text-gray-400" data-id="element-169">
                    {isRTL ? 'لا توجد رسائل بعد' : 'No messages yet'}
                  </p>
                </div> : messages.map((msg, index) => {
            const isMe = msg.fromMe;
            const showDate = index === 0 || new Date(msg.sentAt).toDateString() !== new Date(messages[index - 1].sentAt).toDateString();
            return <Fragment key={msg.id || index} data-id="element-170">
                      {showDate && <div className="flex justify-center my-3" data-id="element-171">
                          <span className="bg-white text-gray-500 text-[11px] px-3 py-1 rounded-lg shadow-sm" data-id="element-172">
                            {formatDate(msg.sentAt)}
                          </span>
                        </div>}
                      <div className={`flex ${isMe ? 'justify-end' : 'justify-start'}`} data-id="element-173">
                        <div className={`max-w-[75%] sm:max-w-[65%] rounded-lg px-3 py-2 shadow-sm ${isMe ? 'bg-[#d9fdd3] rounded-tr-none' : 'bg-white rounded-tl-none'}`} data-id="element-174">
                          <p className="text-[13px] text-gray-800 whitespace-pre-wrap leading-relaxed" data-id="element-175">
                            {msg.text}
                          </p>
                          <div className="flex items-center justify-end gap-1 mt-0.5" data-id="element-176">
                            <span className="text-[10px] text-gray-400" data-id="element-177">
                              {formatTime(msg.sentAt)}
                            </span>
                            {isMe && <CheckCheck className="w-3 h-3 text-blue-400" data-id="element-178" />}
                          </div>
                        </div>
                      </div>
                    </Fragment>;
          })}
              <div ref={messagesEndRef} data-id="element-179" />
            </div>

            {/* Input Area */}
            <form onSubmit={handleSendMessage} className="bg-white p-3 border-t border-gray-200 flex items-end gap-2 flex-shrink-0" data-id="element-180">
              <button type="button" className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100" data-id="element-181">
                <Plus className="w-5 h-5" data-id="element-182" />
              </button>
              <div className="flex-1 bg-gray-100 rounded-2xl px-4 py-2 focus-within:ring-1 focus-within:ring-[#4f1964] transition-shadow" data-id="element-183">
                <textarea value={messageInput} onChange={e => setMessageInput(e.target.value)} onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }} placeholder={isRTL ? 'اكتب رسالة...' : 'Type a message...'} className="w-full bg-transparent border-none focus:ring-0 focus:outline-none resize-none max-h-32 text-sm py-1" rows={1} dir={isRTL ? 'rtl' : 'ltr'} data-id="element-184" />
              </div>
              <button type="submit" disabled={!messageInput.trim() || isSending} className={`p-2.5 rounded-full flex-shrink-0 transition-all ${messageInput.trim() ? 'bg-[#4f1964] text-white hover:bg-[#3d1350] shadow-md' : 'bg-gray-100 text-gray-400'}`} data-id="element-185">
                {isSending ? <Loader2 className="w-5 h-5 animate-spin" data-id="element-186" /> : <Send className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} data-id="element-187" />}
              </button>
            </form>
          </> /* Empty State - No Chat Selected */ : <div className="flex-1 flex flex-col items-center justify-center bg-[#f0f2f5] text-center p-8" data-id="element-188">
            <div className="w-48 h-48 bg-white rounded-full flex items-center justify-center mb-6 shadow-sm" data-id="element-189">
              <MessageSquare className="w-20 h-20 text-gray-200" data-id="element-190" />
            </div>
            <h2 className="text-2xl font-light text-gray-600 mb-3" data-id="element-191">
              {isRTL ? 'مرحباً بك في المحادثات' : 'Welcome to Conversations'}
            </h2>
            <p className="text-gray-400 max-w-sm text-sm" data-id="element-192">
              {isRTL ? 'اختر محادثة من القائمة للبدء في عرض الرسائل والرد عليها.' : 'Select a conversation from the list to view messages and reply.'}
            </p>
            <div className="mt-6 flex items-center gap-2 text-xs text-gray-300" data-id="element-193">
              <Lock className="w-3 h-3" data-id="element-194" />
              {isRTL ? 'مشفرة من الطرف إلى الطرف' : 'End-to-end encrypted'}
            </div>
          </div>}
      </div>
    </div>;
}